/**
 * RevenueCat Web Billing wrapper.
 * ─────────────────────────────────────────────────────────────────────────
 * Browsers cannot use Apple StoreKit / Google Play Billing — those are
 * native-only APIs. RevenueCat's Web Billing product fills the gap by
 * routing the *web* purchase through Stripe, while keeping the customer
 * record in the SAME RevenueCat customer that the iOS/Android apps use.
 * That means an entitlement granted on the website (e.g. "annual_pro")
 * shows up in the user's RC profile alongside any future App Store /
 * Play Store entitlements, and the existing webhook → DB sync (the one
 * powering `User.current_subscription`) keeps working unchanged.
 *
 * What this module does NOT do:
 *   - It does not configure RC eagerly at app boot (the SDK pulls in
 *     Stripe.js + analytics, ~120 KB gzipped, that we'd waste on every
 *     anonymous dashboard hit). We `import()` the SDK lazily only on
 *     the sign-up flow, the only place we open the paywall today.
 *   - It does not persist anything in Redux. Subscription state is the
 *     authoritative responsibility of the API (`User.current_subscription`)
 *     synced via RC's webhook; the SDK in the browser is just a checkout
 *     surface.
 *
 * Required env (configure on the host that builds the dashboard):
 *
 *   REACT_APP_REVENUECAT_WEB_API_KEY
 *     → RevenueCat *web-platform* public SDK key. Find it in the RC
 *       dashboard under "Project Settings → API keys → Web Billing".
 *       This is NOT the same key as the iOS/Android one.
 *
 *   REACT_APP_REVENUECAT_ANNUAL_PACKAGE
 *     → Optional. Identifier of the annual package inside the *current*
 *       offering (e.g. `"$rc_annual"`, `"annual_2026"`). Defaults to
 *       `"$rc_annual"` (RC's reserved annual identifier) which is what
 *       the dashboard offering ships with by default. Override only if
 *       your offering uses a custom package id.
 *
 *   REACT_APP_REVENUECAT_OFFERING
 *     → Optional. Specific offering id to fetch. Leave unset to use the
 *       project's "current" offering — usually what you want.
 */

const ANNUAL_PACKAGE_ID =
  process.env.REACT_APP_REVENUECAT_ANNUAL_PACKAGE || "$rc_annual";

const OFFERING_ID = process.env.REACT_APP_REVENUECAT_OFFERING || null;

const WEB_API_KEY = process.env.REACT_APP_REVENUECAT_WEB_API_KEY || "";

/**
 * Whether the build has the keys needed to actually open a checkout.
 * Used by the UI to swap the "Continue with Annual" button for a
 * disabled state + a clear "ask the admin to configure RC" hint.
 */
export const isWebBillingConfigured = () => Boolean(WEB_API_KEY);

/*
  Module-level cache so we don't re-`Purchases.configure()` on every
  user gesture. Configure is idempotent for the same `appUserId` but it
  re-fetches offerings + customer info under the hood, which is wasted
  network on the second click.
*/
let cachedClient = null;
let cachedClientUserId = null;

const loadClient = async (appUserId) => {
  if (!isWebBillingConfigured()) {
    throw new Error(
      "RevenueCat Web Billing is not configured for this build. Set " +
        "REACT_APP_REVENUECAT_WEB_API_KEY and rebuild the dashboard."
    );
  }
  if (cachedClient && cachedClientUserId === appUserId) {
    return cachedClient;
  }
  // Lazy import: this is the only place we pull the SDK + Stripe.js
  // chunk. Splitting it keeps the dashboard's first-paint bundle slim.
  const { Purchases } = await import("@revenuecat/purchases-js");
  cachedClient = Purchases.configure(WEB_API_KEY, appUserId);
  cachedClientUserId = appUserId;
  return cachedClient;
};

/**
 * Resolve the annual package the user is allowed to purchase.
 *
 * The RC SDK splits offerings into *packages* (a price + a duration);
 * we hunt for the one whose identifier matches `ANNUAL_PACKAGE_ID`,
 * falling back to RC's reserved `$rc_annual` slot, then to the first
 * yearly package we can find. That last fallback covers projects that
 * only have one offering with a single yearly product and no
 * canonical id set.
 */
const findAnnualPackage = (offerings) => {
  const offering = OFFERING_ID
    ? offerings.all?.[OFFERING_ID]
    : offerings.current;
  if (!offering) return null;

  const direct = offering.availablePackages?.find(
    (p) => p.identifier === ANNUAL_PACKAGE_ID
  );
  if (direct) return direct;

  const annualSlot = offering.annual;
  if (annualSlot) return annualSlot;

  const yearly = offering.availablePackages?.find(
    (p) =>
      p.packageType === "ANNUAL" ||
      p.rcBillingProduct?.normalPeriodDuration === "P1Y"
  );
  return yearly || null;
};

/**
 * Open the RC-hosted Stripe checkout for the annual subscription.
 *
 * Resolves with `{ ok: true, customerInfo }` once the entitlement is
 * granted, or `{ ok: false, code, error }` on any failure. We don't
 * `throw`, because the sign-up screen wants a tidy banner instead of a
 * crashed component.
 *
 * `appUserId` MUST be the same id that the API records on the user
 * (we use the just-issued `user.id` from POST /auth/register). RC keys
 * the customer record off this id, so reusing it across web + app is
 * what links a website-paid student to their phone install.
 */
export const startAnnualCheckout = async ({
  appUserId,
  email,
  entitlementKey = "pro",
}) => {
  if (!appUserId) {
    return {
      ok: false,
      code: "NO_USER",
      error: new Error("startAnnualCheckout called without appUserId."),
    };
  }
  try {
    const client = await loadClient(appUserId);
    const offerings = await client.getOfferings();
    const annual = findAnnualPackage(offerings);
    if (!annual) {
      return {
        ok: false,
        code: "NO_ANNUAL_PACKAGE",
        error: new Error(
          "No annual package found in the active RevenueCat offering. " +
            "Configure an annual package in the RC dashboard or set " +
            "REACT_APP_REVENUECAT_ANNUAL_PACKAGE."
        ),
      };
    }

    /*
      `purchase` opens RC's Stripe-backed checkout iframe. It resolves
      ONLY on a completed purchase; cancellation rejects with a
      `UserCancelledError` we translate into `code: "CANCELLED"` so
      the UI can stay quiet (no error toast for user-initiated cancels).
    */
    await client.purchase({ rcPackage: annual, customerEmail: email });

    const customerInfo = await client.getCustomerInfo();
    const entitled = Boolean(customerInfo?.entitlements?.active?.[entitlementKey]);
    if (!entitled) {
      return {
        ok: false,
        code: "ENTITLEMENT_NOT_GRANTED",
        error: new Error(
          `RevenueCat reports the purchase succeeded but the "${entitlementKey}" ` +
            "entitlement is still inactive. Check that the annual product is " +
            "attached to that entitlement in the RC dashboard."
        ),
        customerInfo,
      };
    }
    return { ok: true, customerInfo };
  } catch (error) {
    const name = error?.name || error?.code || "";
    if (
      name === "UserCancelledError" ||
      String(error?.message || "").toLowerCase().includes("cancel")
    ) {
      return { ok: false, code: "CANCELLED", error };
    }
    return { ok: false, code: "UNKNOWN", error };
  }
};
